import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIonRouter } from "@ionic/react";
import {
  getOrganisations,
  getVisibleOrganisations,
  switchOrganisation,
  acceptInvitation,
  declineInvitation,
  requestAccess,
} from "@/api/organisations";
import { useAuthStore } from "./useAuth";
import { pushNotificationService } from "@services/pushNotifications";
import { report } from "@services/Sentry.ts";
import { Did } from "@/types";

/**
 * Hook to fetch user's organisations
 */
export const useOrganisations = () => {
  return useQuery({
    queryKey: ["organisations"],
    queryFn: getOrganisations,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch user's organisations
 */
export const useVisibleOrganisations = () => {
  return useQuery({
    queryKey: ["visible-organisations"],
    queryFn: getVisibleOrganisations,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to switch organisation
 */
export const useSwitchOrganisation = () => {
  const queryClient = useQueryClient();
  const router = useIonRouter();
  const { setCurrentOrganisation, setOrganisationSettings } = useAuthStore();

  return useMutation({
    mutationFn: (organisationId: Did) => switchOrganisation(organisationId),
    onSuccess: (data) => {
      setCurrentOrganisation(data.organisation);

      if (data.settings) {
        setOrganisationSettings(data.settings);
      }

      // Initialize push notifications for PWA
      pushNotificationService.initialize().catch((e: unknown) => {
        console.error(e);
        report(e);
      });

      // Invalidate queries that depend on organisation
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
      queryClient.invalidateQueries({ queryKey: ["organisation-settings"] });

      // Navigate to main app
      router.push("/tabs", "root", "replace");
    },
  });
};

/**
 * Hook to accept an invitation
 */
export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: Did) => acceptInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visible-organisations"] });
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
    },
  });
};

/**
 * Hook to decline an invitation
 */
export const useDeclineInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: Did) => declineInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visible-organisations"] });
    },
  });
};

/**
 * Hook to request access to an organisation
 */
export const useRequestAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organisationId,
      message,
    }: {
      organisationId: Did;
      message?: string;
    }) => requestAccess(organisationId, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visible-organisations"] });
    },
  });
};
